import {useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useRentalAssetTypes} from 'src/assets/rental-assets/use-rental-asset-types'
import {useWorkerTypes} from 'src/assets/worker-types/use-worker-types'
import {useCurrentUser} from 'src/auth/use-current-user'
import {useCustomerSavedLocations} from 'src/customers/saved-locations/use-saved-locations'
import {useCustomer} from 'src/customers/use-customers'
import {getJob} from 'src/jobs/api'
import {loadDraft} from 'src/jobs/create-job-storage'
import {jobKeys} from 'src/jobs/query-keys'
import {createInitialFormValues, type CreateJobFormValues} from 'src/jobs/types'
import {useJob} from 'src/jobs/use-jobs'
import type {Quote} from 'src/quotes/schemas'
import {useQuote} from 'src/quotes/use-quotes'
import {useActiveServiceTypes} from 'src/settings/use-service-types'

import {
    type AddToOrder,
    isBlankJobEntry,
    linesBilled,
    prefillAddToOrder,
    prefillFromJob,
    prefillFromQuote,
} from './job-entry-logic'
import {locationFromSavedSite} from './saved-sites'

export type JobEntryStart = {
    values: CreateJobFormValues
    customerName: string | null
    restored: boolean
    ratesCleared: boolean
    copyFailed: 'source-job' | 'clone-job' | null
}

type JobEntryPrefill =
    | {status: 'loading'; label: string}
    | {status: 'quote-failed'; retry: () => void}
    | {status: 'ready'; start: JobEntryStart; quote: Quote | undefined; quoteUsable: boolean}

type PrefillOptions = {
    storageKey: string
    quoteId: string | null
    order: AddToOrder | null
}

export function useJobEntryPrefill({storageKey, quoteId, order}: PrefillOptions): JobEntryPrefill {
    const currentUser = useCurrentUser()
    const [draft] = useState(() => loadDraft(storageKey))
    const restored = draft?.edited === true && !isBlankJobEntry(draft.values)
    const cloneJobId = draft && !draft.edited ? draft.values.cloneJobId : null

    const workerTypes = useWorkerTypes()
    const assetTypes = useRentalAssetTypes()
    const categories = useActiveServiceTypes()
    const quote = useQuote(quoteId ?? '', {enabled: Boolean(quoteId)})
    const quoteUsable = quote.data?.status === 'accepted' && quote.data.projectId == null
    const fillFromQuote = quoteUsable && !restored
    const quoteCustomerId = quote.data?.customerId ?? ''
    const quoteCustomer = useCustomer(quoteCustomerId)
    const quoteSites = useCustomerSavedLocations(quoteCustomerId, undefined, {
        enabled: fillFromQuote,
    })
    const sourceJobId = order && !restored ? order.sourceJobId : null
    const sourceJob = useJob(sourceJobId ?? '')
    const cloneJob = useJob(cloneJobId ?? '')

    if (quoteId && (quote.isError || quoteCustomer.isError)) {
        return {
            status: 'quote-failed',
            retry: () => void (quote.isError ? quote.refetch() : quoteCustomer.refetch()),
        }
    }
    const typesFetched = workerTypes.isFetched && assetTypes.isFetched && categories.isFetched
    if (
        (quoteId && quote.isPending) ||
        (fillFromQuote && (!quoteCustomer.data || !typesFetched || !quoteSites.isFetched))
    ) {
        return {status: 'loading', label: 'Loading the quote'}
    }
    if ((sourceJobId && sourceJob.isPending) || (cloneJobId && cloneJob.isPending)) {
        return {status: 'loading', label: 'Loading the job to copy'}
    }

    const initial = draft?.values ?? createInitialFormValues()
    const base: CreateJobFormValues = {
        ...initial,
        ...(order
            ? {workMode: 'existing', projectId: order.projectId, customerId: order.customerId}
            : {workMode: 'new', projectId: null}),
        internalContactId:
            initial.internalContactId ??
            (!order && currentUser?.isSalesRep ? currentUser.id : null),
    }
    const start: JobEntryStart = {
        values: base,
        customerName: order?.customerName ?? draft?.customerName ?? null,
        restored,
        ratesCleared: false,
        copyFailed: null,
    }
    const buildReadyPrefill = (next: JobEntryStart): JobEntryPrefill => ({
        status: 'ready',
        start: next,
        quote: quote.data,
        quoteUsable,
    })
    const applyLinesBilledToStart = (values: CreateJobFormValues): JobEntryStart => {
        const lines = linesBilled(values.billingMethod, values, assetTypes.data)
        return {...start, values: {...values, ...lines.lines}, ratesCleared: lines.cleared}
    }

    if (fillFromQuote && quote.data && quoteCustomer.data) {
        const prefill = prefillFromQuote(quote.data, base, {
            workerTypes: workerTypes.data ?? [],
            assetTypes: assetTypes.data ?? [],
            categories: categories.data ?? [],
        })
        const [onlySite, ...otherSites] = quoteSites.data ?? []
        const locations =
            base.locations.length === 0 && onlySite && otherSites.length === 0
                ? [locationFromSavedSite(onlySite)]
                : base.locations
        return buildReadyPrefill({
            ...applyLinesBilledToStart({...base, ...prefill, locations}),
            customerName: quoteCustomer.data.name,
        })
    }
    if (order && !restored) {
        const orderBase: CreateJobFormValues = {
            ...base,
            dispatchYardId: order.dispatchYardId,
            yardPickedByRep: order.dispatchYardId != null,
        }
        if (sourceJobId && !sourceJob.data) {
            return buildReadyPrefill({...start, values: orderBase, copyFailed: 'source-job'})
        }

        return buildReadyPrefill({
            ...start,
            values: sourceJob.data
                ? prefillAddToOrder(sourceJob.data, orderBase, {
                      today: businessCalendarDate(),
                      dispatchYardId: order.dispatchYardId,
                  })
                : orderBase,
        })
    }
    if (cloneJobId) {
        if (!cloneJob.data) {
            return buildReadyPrefill({
                ...start,
                values: {...base, cloneJobId: null},
                copyFailed: 'clone-job',
            })
        }
        return buildReadyPrefill(
            applyLinesBilledToStart({
                ...base,
                ...prefillFromJob(cloneJob.data, base.scheduling),
                cloneJobId,
            }),
        )
    }
    return buildReadyPrefill(start)
}

/** Fetch a job detail, bypassing the query cache. */
export function useFetchJob() {
    const queryClient = useQueryClient()

    return (jobId: string) =>
        queryClient.fetchQuery({
            queryKey: jobKeys.detail(jobId),
            queryFn: () => getJob(jobId),
            staleTime: 0,
        })
}
